/** Glossary + causal explainers for teaching.
 *  Next backlog teach 154–158, 160–161. */

export const GLOSSARY = {
  'δ¹³C': 'Carbon isotope ratio — tracks organic burial vs carbonate. Heavy values mean more burial.',
  'δ¹⁸O': 'Oxygen isotope ratio — proxy for ice volume and temperature.',
  euxinia: 'Sulfidic anoxic water — mid-depth ocean poisoned by H₂S, starves metals and nitrogenase.',
  NPP: 'Net primary productivity — carbon fixed by photosynthesis minus plant respiration.',
  Chappuis: 'Ozone absorption bands that turn twilight deep blue instead of orange.',
  allopatric: 'Speciation by geographic split — a barrier cuts gene flow until lineages diverge.',
  Redfield: 'C:N:P ≈ 106:16:1 in marine plankton — whoever runs out first limits the bloom.',
  redox: 'Who donates and who accepts electrons — the Archean’s real ladder.',
  LUCA: 'Last universal common ancestor — the root of your live phylogeny.',
  GOE: 'Great Oxidation Event — free O₂ rises once sinks are overwhelmed; anaerobes die.',
  CCD: 'Carbonate compensation depth — below it, shells dissolve rather than bury.',
  AMOC: 'Atlantic-style overturning — dense water sinks at high latitudes and the return flow carries heat north. Freshwater can shut the conveyor.',
  Ekman: 'Wind-driven transport ninety degrees off the wind — right in the north, left in the south. It is why eastern coasts upwell.',
  Sverdrup: 'The interior of an ocean basin moves meridionally in proportion to the curl of the wind stress. Gyres fall out of this balance.',
  'western boundary': 'The return flow of a gyre concentrates into a narrow jet on the western edge of the basin — Gulf Stream, Kuroshio, Agulhas.',
  stack: 'An ordered list of materials with thickness. The top is the surface; erosion peels it and deposition writes a new layer.',
  unconformity: 'A gap in the stack — erosion removed layers and deposition resumed. The age jump is the story.',
  illuminant: 'The light a surface is seen in. The same rock is a different colour under a 2,560 K dwarf than under the Sun. White balance here is a camera calibrated to the Sun — the star tints the picture; exposure handles brightness.',
  'rain shadow': 'Air forced up a windward slope rains out; the lee descends, warms and dries. Deserts sit in that shadow.',
  isostasy: 'Crust floats on the mantle. Thicken it and the surface rises; unload it and the remaining peaks rise too.',
  'Bjerknes': 'The coupling that makes El Niño: stronger trades deepen the east–west SST contrast, which strengthens the trades.',
  Walker: 'The tropical east–west cell — rising over the warm pool, sinking over the cold tongue. ENSO is this cell breathing.',
  ENSO: 'El Niño–Southern Oscillation. The thermocline tilts, trades slacken, the east Pacific warms, and rainfall swaps oceans.',
  vorticity: 'How much the air is spinning. Cyclones are patches of vorticity; the jet is a ribbon of it.',
  'shallow water': 'A one-layer fluid: height plus two velocities. Rotation turns a height gradient into a current. Not a GCM.',
  'mixed layer': 'The stirred top of the ocean. Wind and convection deepen it; summer warming shoals it. Maritime climates live here.',
  fetch: 'How far the wind has blown over open water. Fetch, not wind speed alone, is why some coasts have surf and others do not.',
  monsoon: 'A seasonal reversal driven by land heating faster than the sea. Summer continents inhale; winter they exhale.',
  'magma chamber': 'A volume under a volcano with a roof that can fail. Empty it fast enough and the mountain becomes a hole.',
  'dynamic topography': 'Mantle flow lifts the surface over upwellings and pulls it down over slabs — hundreds of metres, continent-wide.',
  Kleiber: 'Metabolic rate scales as mass^¾ — sets density and lifespan.',
  Whittaker: 'Biome diagram in temperature–precipitation space.',
  springs: 'Syzygy tides — Moon and Sun aligned; lunar and solar bulges add.',
  neaps: 'Quadrature tides — Moon and Sun at right angles; range is smallest.',
  intertidal: 'Cells alternately wet and dry each lunar day — desiccation selects hard here.',
  Roche: 'Inside ~2.9 planetary radii a moon is torn apart; the lever clamps to that floor.',
  chaos: 'Weather predictability ceiling is ~2 weeks — beyond that, forecasts are scenarios not predictions.',
  STP: 'Significant tornado parameter — product of CAPE, storm-relative helicity, LCL height, and shear. High values correlate with violent tornadoes.',
  EF: 'Enhanced Fujita scale 0–5, rating tornado intensity by damage. Most tornadoes are weak (EF0–1).',
  derecho: 'A widespread, long-lived windstorm produced by a bow echo — a squall line that bows outward.',
  'cold wake': 'Cooled sea surface left behind a tropical cyclone. Upwelling and mixing chill the track, weakening the next storm that crosses it.',
  PI: 'Potential intensity — the thermodynamic ceiling on a tropical cyclone set by SST and outflow temperature. No storm can exceed it.',
  waterspout: 'A tornado over water, usually fair-weather and weak. Warm SST and low LCL favour them.',
  landspout: 'A weak, non-supercell tornado — low-level convergence spins up a vortex without the mesocyclone a classic tornado needs.',
  'dust devil': 'A convective vortex over hot, dry ground. Not a tornado — no parent thunderstorm. Mars has large ones.',
  shear01: '0–1 km wind shear — the change in wind speed and direction in the lowest kilometre. High values can spin up tornadoes even when CAPE is modest.',
  CAPE: 'Convective available potential energy — how much fuel the atmosphere has to overturn. High CAPE means thunderstorms, hail, tornadoes.',
  CIN: 'Convective inhibition — the cap that holds storms down. Low CIN means they fire easily; high CIN means something has to break the lid.',
  LCL: 'Lifted condensation level — where a rising parcel first makes cloud. Low LCL means low cloud base, which helps tornadoes reach the ground.',
  PWAT: 'Precipitable water — the total water in the column if you wrung it out. High values feed the heaviest rain.',
  hodograph: 'Wind plotted at each height. A turning wind means the environment can spin a storm; a straight hodograph splits it in two.',
  sounding: 'A vertical profile of temperature, moisture and wind — the data a weather balloon returns. The sounding tells you what the sky wants to do.',
  virga: 'Rain that evaporates before reaching the ground — visible as dark streaks under a cloud. Dry mid-levels cause it.',
  drought: 'A memory of missing rain — what the soil and biosphere feel when the atmosphere sinks for months. Different from dry: a desert is dry, but not in drought.',
  reflectivity: 'What a weather radar sees — water drops scattering energy back. Green is light rain, yellow is moderate, red is heavy, purple is "take shelter now".',
  convection: 'Air rising because it is warmer than its surroundings — the engine of every thunderstorm. The sounding measures whether it can start; the forecast asks whether it will.',
};

export function defineTerm(term) {
  return GLOSSARY[term] || GLOSSARY[term?.replace?.(/^./, (c) => c.toUpperCase())] || null;
}

/** Short unprompted explanation when drama happens. */
export function explainDrama(W) {
  if (W._ensoPhase === 'El Niño') {
    return { title: 'El Niño', body: 'Trades have slackened. The east tropical ocean is warm, the thermocline there is deep, and rain has followed the warmth across the basin.', settle: 'months–a few years' };
  }
  if (W._ensoPhase === 'La Niña') {
    return { title: 'La Niña', body: 'Trades are piled high. The east is a cold tongue, upwelling is fierce, and the west is wetter than its mean.', settle: 'a year or two' };
  }
  if (W._conveyorNote && W.conveyor < 0.4) {
    return { title: 'Overturning weakening', body: 'Fresh, light surface water is capping the dense deep. The conveyor slows — high latitudes cool, and the heat that used to ride north stays in the tropics.', settle: 'decades–centuries' };
  }
  if (W.state === 'snowball') {
    return { title: 'Snowball', body: 'Ice–albedo feedback ran away. Volcanoes must rebuild CO₂ under the lid before melt.', settle: '10⁵–10⁶ yr' };
  }
  if (W.state === 'moist-greenhouse') {
    return { title: 'Moist greenhouse', body: 'Water is reaching altitudes where it can escape. The thermostat may not save this.', settle: 'irreversible on human scales' };
  }
  if ((W.gases?.O2 || 0) > 0.05 && W._oxEvent) {
    return { title: 'Oxygen rising', body: 'Burial outran the sinks. Free O₂ is a poison to the old biosphere and fuel for the new.', settle: 'Myr' };
  }
  if ((W._extinctionPulse || 0) > 0.5) {
    return { title: 'Extinction pulse', body: 'A kill mechanism crossed a threshold — thermal, hypoxic, acid, UV, or habitat. Check the chronicle for attribution.', settle: 'recovery 1–10 Myr' };
  }
  return null;
}

export const READING_LIST = [
  { author: 'Lovelock', title: 'Gaia', note: 'Regulation without foresight' },
  { author: 'Lenton', title: 'Earth System Science', note: 'Tipping elements' },
  { author: 'Canfield', title: 'Oxygen', note: 'The redox history' },
  { author: 'Knoll', title: 'Life on a Young Planet', note: 'Precambrian biosphere' },
  { author: 'Lane', title: 'The Vital Question', note: 'Energy and complexity' },
  { author: 'Sepkoski', title: 'Marine diversity curves', note: 'Mass extinction notches' },
];

/** Progressive tool unlock by world state. */
export const TOOL_GATES = {
  inspect: () => true,
  core: (W) => true,
  icecore: (W) => (W.iceFrac || 0) > 0.02,
  seedGuild: (W) => (W.habitability || 0) > 0.2,
  seed: (W) => (W.transitions?.abiogenesis || W.meanLife > 0.05),
  raise: () => true,
  lower: () => true,
  solar: () => true,
  co2: () => true,
  albedo: (W) => (W.iceFrac || 0) > 0.05 || (W.meanLife || 0) > 0.1,
  meteor: (W) => (W.ageYr || 0) > 1e8 || !W.rule?.deepTime,
  plague: (W) => (W.meanLife || 0) > 0.15,
  refuge: (W) => (W.tree?.living?.length || 0) > 2,
  plate: (W) => !W.rule?.airless,
  plume: (W) => !W.rule?.airless,
  buster: (W) => (W.attribution?.acts || 0) > 10,
};

const LAND_TOOLS = new Set([
  'raise', 'lower', 'flatten', 'smooth', 'sharpen', 'roughen',
  'crust', 'plume', 'plate', 'river',
]);

export function toolsUnlocked(W) {
  const out = {};
  for (const [id, gate] of Object.entries(TOOL_GATES)) out[id] = !!gate(W);
  if (W?.noSurface) {
    for (const id of LAND_TOOLS) out[id] = false;
  }
  return out;
}
